import { useState, useEffect } from 'react';
import { useCreateMovementType, useUpdateMovementType } from '../../../hooks/useMovementTypes';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
} from '../../../common/components';
import { validateMovementType, prepareMovementTypeData } from '../utils/movementTypeValidation';
import { INITIAL_MOVEMENT_TYPE_STATE } from '../constants/movementTypesConstants';
import GeneralDataSection from './GeneralDataSection';

const MovementTypeDialog = ({ open, onClose, editMode, selectedMovementType }) => {
  const [formData, setFormData] = useState(INITIAL_MOVEMENT_TYPE_STATE);
  const [errors, setErrors] = useState({});

  // Hooks de mutación
  const createMovementTypeMutation = useCreateMovementType();
  const updateMovementTypeMutation = useUpdateMovementType();

  // Inicializar datos del formulario
  useEffect(() => {
    if (editMode && selectedMovementType) {
      setFormData({
        description: selectedMovementType.description || '',
        nature: selectedMovementType.nature || 'debit',
        display_in_orders: Boolean(selectedMovementType.display_in_orders),
        is_bank_movement_type:
          selectedMovementType.is_bank_movement_type !== undefined
            ? Boolean(selectedMovementType.is_bank_movement_type)
            : true,
      });
    } else {
      setFormData(INITIAL_MOVEMENT_TYPE_STATE);
    }
    setErrors({});
  }, [selectedMovementType, editMode, open]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error del campo si existe
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    // Validar formulario
    const validationErrors = validateMovementType(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = prepareMovementTypeData(formData);
      if (editMode) {
        await updateMovementTypeMutation.mutateAsync({
          id: selectedMovementType.uuid,
          data: payload,
        });
      } else {
        await createMovementTypeMutation.mutateAsync(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar tipo de movimiento:', error);
    }
  };

  const isLoading = createMovementTypeMutation.isPending || updateMovementTypeMutation.isPending;

  return (
    <DialogContainer open={open} onClose={onClose} size='small'>
      <DialogTitle>
        {editMode ? 'Editar Tipo de Movimiento' : 'Crear Tipo de Movimiento'}
      </DialogTitle>

      <DialogBody>
        <GeneralDataSection
          formData={formData}
          errors={errors}
          onChange={handleInputChange}
          isEditing={editMode}
        />
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        saveText={editMode ? 'Actualizar' : 'Crear'}
        isSaving={isLoading}
      />
    </DialogContainer>
  );
};

export default MovementTypeDialog;
